import React, { useEffect, useState } from 'react'
import AdminLayout from '../../layouts/AdminLayout'
import toast from 'react-hot-toast'
import Pagination from '../../components/common/Pagination'
import { userService } from '@services/api'
import apiClient from '@services/api/client'

const storageURL = import.meta.env.VITE_STORAGE_URL || 'http://localhost:84/storage'
const mediaUrl = (p) => { if (!p) return null; let s = String(p); const i = s.indexOf('/storage/'); if (i !== -1) s = s.slice(i + 9); return storageURL + '/' + s.replace(/^\/+/, '') }
const getPhotoUrl = (p) => !p ? null : p.startsWith('http') ? p : mediaUrl(p)

// Normalise un user depuis l'API vers la structure attendue par la page
const normalizeUser = (u) => {
  const roleName = typeof u.role === 'object' ? u.role?.name : u.role || ''
  return {
    ...u,
    telephone: u.telephone || u.phone || u.numero || '',
    roles:     roleName || u.roles || '',
    date_inscription: u.date_inscription || u.created_at || '',
    photo_profil: getPhotoUrl(u.photo_profil || u.photo) || '',
  }
}

const AdminUsersPage = () => {
  const [users, setUsers] = useState([])
  const [roles, setRoles] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterRole, setFilterRole] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [sortBy, setSortBy] = useState('date_desc')
  const [filterByPublications, setFilterByPublications] = useState('all')
  const [filterByPhone, setFilterByPhone] = useState('all')
  const [showRoleModal, setShowRoleModal] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [showAddUserModal, setShowAddUserModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showArchiveModal, setShowArchiveModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [newUser, setNewUser] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    mot_de_passe: '',
    confirm_password: '',
    selectedRoles: []
  })
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [selectedUsers, setSelectedUsers] = useState([])
  const [selectAll, setSelectAll] = useState(false)
  
  // États de pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(6)

  useEffect(() => {
    loadUsers()
    loadRoles()

    // Écouter l'événement d'ouverture du modal depuis le layout
    const handleOpenModal = () => setShowAddUserModal(true)
    window.addEventListener('openAddUserModal', handleOpenModal)

    return () => {
      window.removeEventListener('openAddUserModal', handleOpenModal)
    }
  }, [])

  const loadUsers = async () => {
    try {
      const result = await userService.getUsers()
      if (result) {
        const raw = result.data || result.clients || []
        setUsers(raw.map(normalizeUser))
      }
    } catch (error) {
      console.error('Error loading users:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadRoles = async () => {
    try {
      const result = await apiClient.get('/roles')
      setRoles(result.data || result.roles || [])
    } catch (error) {
      console.error('Error loading roles:', error)
    }
  }

  const handleAssignRole = async (userId, roleId) => {
    try {
      // userId peut être un nombre direct ou au format "u_1" / "c_3"
      const realId = String(userId).includes('_') ? String(userId).split('_')[1] : userId

      await apiClient.post('/roles/assign', { user_id: realId, role_id: roleId })
      toast.success('Rôle assigné avec succès')
      loadUsers()
      setShowRoleModal(false)
    } catch (error) {
      console.error('Error assigning role:', error)
      toast.error('Erreur lors de l\'assignation du rôle')
    }
  }

  const handleRemoveRole = async (userId, roleId) => {
    const confirmed = await new Promise((resolve) => {
      toast((t) => (
        <div className="flex flex-col gap-3">
          <p className="font-semibold">Retirer ce rôle à l'utilisateur ?</p>
          <div className="flex gap-2">
            <button
              onClick={() => { toast.dismiss(t.id); resolve(true); }}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors font-medium"
            >
              Oui, retirer
            </button>
            <button
              onClick={() => { toast.dismiss(t.id); resolve(false); }}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors font-medium"
            >
              Annuler
            </button>
          </div>
        </div>
      ), { duration: Infinity });
    });

    if (!confirmed) return;

    try {
      const realId = String(userId).includes('_') ? String(userId).split('_')[1] : userId

      await apiClient.post('/roles/remove', { user_id: realId, role_id: roleId })
      toast.success('✅ Rôle retiré avec succès')
      loadUsers()
    } catch (error) {
      console.error('Error removing role:', error)
      toast.error('Erreur lors du retrait du rôle')
    }
  }

  const handleViewUser = (user) => {
    setSelectedUser(user)
    setShowViewModal(true)
  }

  const handleArchiveUser = async (userId, isArchived) => {
    try {
      const result = await apiClient.put('/users/' + userId + '/archive', {})
      toast.success(result?.message || 'Opération réussie')
      loadUsers()
      setShowArchiveModal(false)
      setSelectedUser(null)
    } catch (error) {
      console.error('Error archiving user:', error)
      toast.error(`Erreur lors de l'archivage de l'utilisateur`)
    }
  }

  const handleDeleteUser = async (userId, userName) => {
    try {
      await userService.deleteUser(userId)
      toast.success('Utilisateur supprimé')
      loadUsers()
      setShowDeleteModal(false)
      setSelectedUser(null)
    } catch (error) {
      console.error('Error deleting user:', error)
      toast.error(error.response?.data?.message || 'Erreur lors de la suppression de l\'utilisateur')
    }
  }

  const handleAddUser = async () => {
    // Validation
    if (!newUser.nom || !newUser.prenom || !newUser.email || !newUser.mot_de_passe) {
      toast.error('Veuillez remplir tous les champs obligatoires')
      return
    }

    // Validation email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(newUser.email)) {
      toast.error('Veuillez entrer un email valide')
      return
    }

    // Validation mot de passe (minimum 8 caractères)
    if (newUser.mot_de_passe.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères')
      return
    }

    // Validation confirmation mot de passe
    if (newUser.mot_de_passe !== newUser.confirm_password) {
      toast.error('Les mots de passe ne correspondent pas')
      return
    }

    // Validation des rôles sélectionnés
    if (newUser.selectedRoles.length === 0) {
      toast.error('Veuillez sélectionner au moins un rôle pour l\'utilisateur')
      return
    }

    try {
      const res = await userService.createUser({
        nom: newUser.nom,
        prenom: newUser.prenom,
        email: newUser.email,
        phone: newUser.telephone,
        password: newUser.mot_de_passe,
        // L'API valide le mot de passe avec la règle « confirmed »
        password_confirmation: newUser.confirm_password,
        role_id: newUser.selectedRoles[0]
      })

      // Assigner les rôles sélectionnés
      const userId = res?.data?.users_id || res?.user?.users_id || res?.users_id
      if (newUser.selectedRoles.length > 0 && userId) {
        for (const roleId of newUser.selectedRoles) {
          try {
            await apiClient.post('/roles/assign', { user_id: userId, role_id: roleId })
          } catch (roleError) {
            console.error('Erreur lors de l\'assignation du rôle:', roleError)
          }
        }
      }

      toast.success('Utilisateur créé avec succès')
      setShowAddUserModal(false)
      setNewUser({
        nom: '',
        prenom: '',
        email: '',
        telephone: '',
        mot_de_passe: '',
        confirm_password: '',
        selectedRoles: []
      })
      loadUsers()
    } catch (error) {
      console.error('Error adding user:', error)
      // Afficher le détail des erreurs de validation (ex. email déjà utilisé)
      const fieldErrors = error.response?.data?.errors
      const firstFieldError = fieldErrors ? Object.values(fieldErrors).flat()[0] : null
      const errorMessage = firstFieldError || error.response?.data?.message || 'Erreur lors de la création de l\'utilisateur'
      toast.error(errorMessage)
    }
  }

  const filteredUsers = users.filter(user => {
    // Afficher TOUS les utilisateurs de la table users (staff)
    // Même ceux sans rôle assigné pour le moment
    
    const userRoles = user.roles ? user.roles.toLowerCase() : ''
    
    // Exclure uniquement les Clients (si jamais il y en a dans cette table)
    const isClient = userRoles.includes('client')
    if (isClient) return false
    
    // Filtre par statut (actif/archivé/tous)
    if (statusFilter === 'active' && user.archived) return false
    if (statusFilter === 'archived' && !user.archived) return false
    
    // Recherche
    const matchesSearch = user.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.prenom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email?.toLowerCase().includes(searchTerm.toLowerCase())
    
    // Filtre par rôle
    let matchesRole = true
    if (filterRole === 'all') matchesRole = true
    else if (filterRole === '') matchesRole = (!user.roles || user.roles.trim() === '')
    else matchesRole = user.roles?.includes(filterRole)
    
    // Filtre par publications
    let matchesPublications = true
    const postCount = user.post_count || 0
    if (filterByPublications === 'with_posts') matchesPublications = postCount > 0
    else if (filterByPublications === 'without_posts') matchesPublications = postCount === 0
    else if (filterByPublications === 'more_than_5') matchesPublications = postCount > 5
    else if (filterByPublications === 'more_than_10') matchesPublications = postCount > 10
    
    // Filtre par téléphone
    let matchesPhone = true
    if (filterByPhone === 'with_phone') matchesPhone = user.telephone && user.telephone.trim() !== ''
    else if (filterByPhone === 'without_phone') matchesPhone = !user.telephone || user.telephone.trim() === ''
    
    return matchesSearch && matchesRole && matchesPublications && matchesPhone
  })
  
  // Appliquer le tri
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    switch (sortBy) {
      case 'date_desc':
        return new Date(b.date_inscription || 0) - new Date(a.date_inscription || 0)
      case 'date_asc':
        return new Date(a.date_inscription || 0) - new Date(b.date_inscription || 0)
      case 'name_asc':
        return (a.nom || '').localeCompare(b.nom || '')
      case 'name_desc':
        return (b.nom || '').localeCompare(a.nom || '')
      case 'posts_desc':
        return (b.post_count || 0) - (a.post_count || 0)
      case 'posts_asc':
        return (a.post_count || 0) - (b.post_count || 0)
      default:
        return 0
    }
  })
  
  // Calculer les éléments paginés
  const totalPages = Math.ceil(sortedUsers.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedUsers = sortedUsers.slice(startIndex, endIndex)
  
  // Réinitialiser à la page 1 lors d'un changement de filtre
  useEffect(() => {
    setCurrentPage(1)
  }, [searchTerm, filterRole, statusFilter, sortBy, filterByPublications, filterByPhone])
  
  const handlePageChange = (page) => {
    setCurrentPage(page)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
  
  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage)
    setCurrentPage(1)
  }

  const getRoleBadgeColor = (roleName) => {
    const colors = {
      'Super Admin': 'bg-red-100 text-red-700',
      'Admin': 'bg-purple-100 text-purple-700',
      'Superviseur': 'bg-blue-100 text-blue-700',
      'Animateur': 'bg-green-100 text-green-700',
      'Technicien': 'bg-yellow-100 text-yellow-700'
    }
    return colors[roleName] || 'bg-gray-100 text-gray-700'
  }

  const getRoleIconColor = (roleName) => {
    const colors = {
      'Super Admin': { bg: 'bg-red-50', text: 'text-red-600' },
      'Admin': { bg: 'bg-purple-50', text: 'text-purple-600' },
      'Superviseur': { bg: 'bg-blue-50', text: 'text-blue-600' },
      'Animateur': { bg: 'bg-green-50', text: 'text-green-600' },
      'Technicien': { bg: 'bg-yellow-50', text: 'text-yellow-600' }
    }
    return colors[roleName] || { bg: 'bg-gray-50', text: 'text-gray-600' }
  }

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-gray-400 text-lg">Chargement...</div>
        </div>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout>
      <div className="space-y-6 mt-10">
        {/* Header */}
        <div className="bg-white rounded-md border border-slate-200 p-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-semibold text-gray-800">Gestion des Utilisateurs</h1>
              <p className="text-gray-500 mt-2 flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                Gérer les rôles et permissions
              </p>
            </div>
            <button
              onClick={() => setShowAddUserModal(true)}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#FFD700] to-[#F5C800] text-[#002A4E] rounded-md transition-all font-medium"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
              Ajouter un utilisateur
            </button>
          </div>
        </div>

        {/* Stats globales */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Total utilisateurs */}
          <div className="bg-white rounded-md p-6 border border-slate-200 transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium mb-1">Total Utilisateurs</p>
                <p className="text-2xl font-bold text-gray-800">{users.filter(u => {
                  const userRoles = u.roles ? u.roles.toLowerCase() : ''
                  return !userRoles.includes('client')
                }).length}</p>
              </div>
              <div className="w-12 h-12 bg-blue-50 rounded-md flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Utilisateurs actifs */}
          <div className="bg-white rounded-md p-6 border border-slate-200 transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium mb-1">Utilisateurs Actifs</p>
                <p className="text-2xl font-bold text-green-600">
                  {users.filter(u => {
                    const userRoles = u.roles ? u.roles.toLowerCase() : ''
                    return !userRoles.includes('client') && !u.archived
                  }).length}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-50 rounded-md flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          {/* Utilisateurs archivés */}
          <div className="bg-white rounded-md p-6 border border-slate-200 transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium mb-1">Utilisateurs Archivés</p>
                <p className="text-2xl font-bold text-orange-600">
                  {users.filter(u => {
                    const userRoles = u.roles ? u.roles.toLowerCase() : ''
                    return !userRoles.includes('client') && u.archived
                  }).length}
                </p>
              </div>
              <div className="w-12 h-12 bg-orange-50 rounded-md flex items-center justify-center">
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Stats par rôle */}

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {roles.filter(role => role.name !== 'Client').map(role => {
            const count = users.filter(u => {
              const userRoles = u.roles ? u.roles.toLowerCase() : ''
              const isClient = userRoles.includes('client')
              return !isClient && u.roles?.includes(role.name)
            }).length
            const iconColors = getRoleIconColor(role.name)
            return (
              <div key={role.id} className="bg-white rounded-md p-5 border border-slate-200 transition-shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 font-medium">{role.name}</p>
                    <p className="text-2xl font-semibold text-gray-800 mt-1">{count}</p>
                  </div>
                  <div className={`w-10 h-10 ${iconColors.bg} rounded-md flex items-center justify-center`}>
                    <svg className={`w-5 h-5 ${iconColors.text}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* Filtres */}

        <div className="bg-white rounded-md p-6 border border-slate-200">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="relative md:col-span-2 lg:col-span-1">
              <svg className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Rechercher par nom, prénom ou email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-md focus:ring-2 focus:ring-amber-200 focus:border-transparent transition-all"
              />
            </div>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-3 border border-slate-200 rounded-md focus:ring-2 focus:ring-amber-200 focus:border-transparent bg-white transition-all"
            >
              <option value="all">Tous les statuts</option>
              <option value="active">✓ Actifs</option>
              <option value="archived">📦 Archivés</option>
            </select>

            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="px-4 py-3 border border-slate-200 rounded-md focus:ring-2 focus:ring-amber-200 focus:border-transparent bg-white transition-all"
            >
              <option value="all">Tous les rôles</option>
              {roles.filter(role => role.name !== 'Client').map(role => (
                <option key={role.id} value={role.name}>{role.name}</option>
              ))}
              <option value="">Sans rôle</option>
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-3 border border-slate-200 rounded-md focus:ring-2 focus:ring-amber-200 focus:border-transparent bg-white transition-all"
            >
              <option value="date_desc">📅 Plus récents</option>
              <option value="date_asc">📅 Plus anciens</option>
              <option value="name_asc">🔤 Nom A-Z</option>
              <option value="name_desc">🔤 Nom Z-A</option>
              <option value="posts_desc">📝 Plus de publications</option>
              <option value="posts_asc">📝 Moins de publications</option>
            </select>

            <select
              value={filterByPublications}
              onChange={(e) => setFilterByPublications(e.target.value)}
              className="px-4 py-3 border border-slate-200 rounded-md focus:ring-2 focus:ring-amber-200 focus:border-transparent bg-white transition-all"
            >
              <option value="all">Toutes les publications</option>
              <option value="with_posts">Avec publications</option>
              <option value="without_posts">Sans publications</option>
              <option value="more_than_5">Plus de 5 publications</option>
              <option value="more_than_10">Plus de 10 publications</option>
            </select>

            <select
              value={filterByPhone}
              onChange={(e) => setFilterByPhone(e.target.value)}
              className="px-4 py-3 border border-slate-200 rounded-md focus:ring-2 focus:ring-amber-200 focus:border-transparent bg-white transition-all"
            >
              <option value="all">Tous les téléphones</option>
              <option value="with_phone">Avec téléphone</option>
              <option value="without_phone">Sans téléphone</option>
            </select>
          </div>
        </div>

        {/* Table des utilisateurs */}

        <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-800">Liste des utilisateurs</h2>
              <span className="text-sm text-gray-500">{sortedUsers.length} utilisateur{sortedUsers.length > 1 ? 's' : ''}</span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-6 py-3">
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={(e) => {
                        setSelectAll(e.target.checked)
                        if (e.target.checked) {
                          setSelectedUsers(paginatedUsers.map(u => u.users_id))
                        } else {
                          setSelectedUsers([])
                        }
                      }}
                      className="w-4 h-4 text-[#002A4E] border-gray-300 rounded focus:ring-2 focus:ring-amber-200 cursor-pointer"
                    />
                  </th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">N°</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Utilisateur</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Téléphone</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Rôles</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Publications</th>
                  <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Inscription</th>
                  <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paginatedUsers.map((user, index) => {
                  // Supprimer les doublons de rôles
                  const userRoles = user.roles 
                    ? [...new Set(user.roles.split(',').map(r => r.trim()))]
                    : []
                  
                  // Vérifier si l'utilisateur est Super Admin
                  const isSuperAdmin = userRoles.some(role => role.toLowerCase() === 'super admin')
                  
                  // Récupérer l'utilisateur connecté
                  const currentUser = JSON.parse(localStorage.getItem('admin_user') || '{}')
                  const currentUserRoles = currentUser.roles || []
                  const isCurrentUserSuperAdmin = currentUserRoles.some(role => role.toLowerCase() === 'super admin')
                  
                  // Un Super Admin ne peut être modifié que par un autre Super Admin
                  const canModify = !isSuperAdmin || isCurrentUserSuperAdmin
                  
                  return (
                    <tr key={user.users_id} className="hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0">
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(user.users_id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedUsers([...selectedUsers, user.users_id])
                            } else {
                              setSelectedUsers(selectedUsers.filter(id => id !== user.users_id))
                              setSelectAll(false)
                            }
                          }}
                          className="w-4 h-4 text-[#002A4E] border-gray-300 rounded focus:ring-2 focus:ring-amber-200 cursor-pointer"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">{String((currentPage - 1) * itemsPerPage + index + 1).padStart(5, '0')}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="relative w-10 h-10 flex-shrink-0">
                            {user.photo_profil ? (
                              <>
                                <img
                                  src={user.photo_profil}
                                  alt=""
                                  className="w-10 h-10 rounded-full object-cover border-2 border-[#F5B700]/40 shadow-sm absolute inset-0"
                                  onError={(e) => {
                                    e.target.style.display = 'none'
                                    e.target.nextElementSibling.style.display = 'flex'
                                  }}
                                />
                                <div
                                  className="w-10 h-10 bg-gradient-to-br from-[#FFD700] to-[#F5C800] rounded-full items-center justify-center text-[#002A4E] font-bold text-sm"
                                  style={{ display: 'none' }}
                                >
                                  {(user.prenom?.charAt(0) || user.nom?.charAt(0) || '?').toUpperCase()}
                                </div>
                              </>
                            ) : (
                              <div className="w-10 h-10 bg-gradient-to-br from-[#FFD700] to-[#F5C800] rounded-full flex items-center justify-center text-[#002A4E] font-bold text-sm ">
                                {(user.prenom?.charAt(0) || user.nom?.charAt(0) || '?').toUpperCase()}
                              </div>
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-gray-800">{user.prenom} {user.nom}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-700">{user.email}</td>
                      <td className="px-6 py-4 text-gray-600">{user.telephone || 'N/A'}</td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {userRoles.length > 0 ? (
                            userRoles.map((role, idx) => (
                              <span
                                key={idx}
                                className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor(role)}`}
                              >
                                {role}
                              </span>
                            ))
                          ) : (
                            <span className="px-2 py-1 bg-gray-100 text-gray-500 rounded-full text-xs">
                              Aucun rôle
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-600">
                        <span className="font-medium">{user.post_count || 0}</span>
                      </td>
                      <td className="px-6 py-4 text-gray-600 text-sm">
                        {user.date_inscription 
                          ? new Date(user.date_inscription).toLocaleDateString('fr-FR')
                          : 'N/A'
                        }
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          {/* Voir */}
                          <button 
                            onClick={() => handleViewUser(user)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors group relative"
                            title="Voir le profil"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </button>

                          {/* Modifier */}
                          <button
                            onClick={() => {
                              if (canModify) {
                                setSelectedUser(user)
                                setShowRoleModal(true)
                              }
                            }}
                            disabled={!canModify}
                            className={`p-2 rounded-md transition-colors group relative ${
                              canModify 
                                ? 'text-green-600 hover:bg-green-50 cursor-pointer' 
                                : 'text-gray-300 cursor-not-allowed'
                            }`}
                            title={canModify ? "Modifier les rôles" : "Seul un Super Admin peut modifier ce compte"}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>

                          {/* Archiver/Désarchiver */}
                          <button 
                            onClick={() => {
                              if (canModify) {
                                setSelectedUser(user)
                                setShowArchiveModal(true)
                              }
                            }}
                            disabled={!canModify}
                            className={`p-2 rounded-md transition-colors group relative ${
                              canModify 
                                ? 'text-orange-600 hover:bg-orange-50 cursor-pointer' 
                                : 'text-gray-300 cursor-not-allowed'
                            }`}
                            title={canModify ? (user.archived ? "Désarchiver" : "Archiver") : "Seul un Super Admin peut modifier ce compte"}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                            </svg>
                          </button>

                          {/* Supprimer */}
                          <button 
                            onClick={() => {
                              if (canModify) {
                                setSelectedUser(user)
                                setShowDeleteModal(true)
                              }
                            }}
                            disabled={!canModify}
                            className={`p-2 rounded-md transition-colors group relative ${
                              canModify 
                                ? 'text-red-600 hover:bg-red-50 cursor-pointer' 
                                : 'text-gray-300 cursor-not-allowed'
                            }`}
                            title={canModify ? "Supprimer l'utilisateur" : "Seul un Super Admin peut modifier ce compte"}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>

            {filteredUsers.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <div className="text-6xl mb-4">👥</div>
                <p className="text-lg">Aucun utilisateur trouvé</p>
              </div>
            )}
          </div>
          
          {/* Pagination */}
          {filteredUsers.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              itemsPerPage={itemsPerPage}
              totalItems={filteredUsers.length}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
          )}
        </div>
      </div>

      {/* MODALS - Rendus en dehors du conteneur principal pour éviter les problèmes de padding */}
      
      {/* Modal d'ajout d'utilisateur */}
      {showAddUserModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] animate-fadeIn">
          <div className="bg-white rounded-md w-full max-w-3xl m-4 animate-scaleIn border border-slate-200">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-[#FFD700] to-[#F5C800] rounded-md flex items-center justify-center">
                  <svg className="w-6 h-6 text-[#002A4E]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold text-gray-800">Ajouter un nouvel utilisateur</h2>
              </div>
              <button
                onClick={() => {
                  setShowAddUserModal(false)
                  setNewUser({
                    nom: '',
                    prenom: '',
                    email: '',
                    telephone: '',
                    mot_de_passe: '',
                    confirm_password: '',
                    selectedRoles: []
                  })
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="p-6 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-6">
                {/* Nom et Prénom */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Nom <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newUser.nom}
                    onChange={(e) => setNewUser({ ...newUser, nom: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-200 focus:border-transparent transition-all"
                    placeholder="Nom de famille"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Prénom <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={newUser.prenom}
                    onChange={(e) => setNewUser({ ...newUser, prenom: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-200 focus:border-transparent transition-all"
                    placeholder="Prénom"
                  />
                </div>

                {/* Email et Téléphone */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-200 focus:border-transparent transition-all"
                    placeholder="email@exemple.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Téléphone
                  </label>
                  <input
                    type="tel"
                    value={newUser.telephone}
                    onChange={(e) => setNewUser({ ...newUser, telephone: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-200 focus:border-transparent transition-all"
                    placeholder="+225 XX XX XX XX XX"
                  />
                </div>

                {/* Mot de passe */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Mot de passe <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={newUser.mot_de_passe}
                      onChange={(e) => setNewUser({ ...newUser, mot_de_passe: e.target.value })}
                      className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-200 focus:border-transparent transition-all"
                      placeholder="Mot de passe sécurisé"
                      minLength="8"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showPassword ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Minimum 8 caractères requis</p>
                </div>

                {/* Confirmation mot de passe */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Confirmer le mot de passe <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={newUser.confirm_password}
                      onChange={(e) => setNewUser({ ...newUser, confirm_password: e.target.value })}
                      className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-md focus:ring-2 focus:ring-amber-200 focus:border-transparent transition-all"
                      placeholder="Confirmer le mot de passe"
                      minLength="8"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showConfirmPassword ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Doit correspondre au mot de passe</p>
                </div>

                {/* Rôles */}
                <div className="col-span-2">
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Rôles (Staff uniquement) <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {roles.filter(role => role.name !== 'Client').map(role => (
                      <label key={role.id} className="flex items-center gap-2 cursor-pointer px-4 py-3 bg-gray-50 hover:bg-[#FFD700]/5 border border-gray-200 rounded-md transition-all group">
                        <input
                          type="checkbox"
                          checked={newUser.selectedRoles.includes(role.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setNewUser({
                                ...newUser,
                                selectedRoles: [...newUser.selectedRoles, role.id]
                              })
                            } else {
                              setNewUser({
                                ...newUser,
                                selectedRoles: newUser.selectedRoles.filter(id => id !== role.id)
                              })
                            }
                          }}
                          className="w-4 h-4 text-[#B8860B] border-gray-300 rounded focus:ring-amber-200"
                        />
                        <span className="text-sm font-medium text-gray-700 group-hover:text-[#B8860B]">{role.name}</span>
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Sélectionnez un ou plusieurs rôles pour cet utilisateur</p>
                </div>

                {/* Informations sur les privilèges */}
                <div className="col-span-2 mt-4 space-y-3">
                  {/* Privilèges accordés */}
                  <div className="p-4 bg-green-50 border border-green-200 rounded-md flex items-start gap-3">
                    <svg className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-green-800">Privilèges utilisateur staff</p>
                      <ul className="text-xs text-green-600 mt-1 space-y-1">
                        <li>✓ Token API généré pour l'authentification</li>
                        <li>✓ Accès au dashboard d'administration</li>
                        <li>✓ Permissions selon les rôles assignés</li>
                        <li>✓ Peut gérer le système selon ses rôles</li>
                      </ul>
                    </div>
                  </div>

                  {/* Info sécurité */}
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-md flex items-start gap-3">
                    <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-blue-800">Sécurité</p>
                      <p className="text-xs text-blue-600 mt-1">Le mot de passe sera hashé et l'utilisateur recevra un token d'authentification sécurisé</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-4 p-6 border-t border-gray-200 bg-gray-50 rounded-md">
              <button
                onClick={() => {
                  setShowAddUserModal(false)
                  setNewUser({
                    nom: '',
                    prenom: '',
                    email: '',
                    telephone: '',
                    mot_de_passe: '',
                    location: '',
                    website: '',
                    bio: '',
                    selectedRoles: []
                  })
                }}
                className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 transition-colors font-medium"
              >
                Annuler
              </button>
              <button
                onClick={handleAddUser}
                className="px-6 py-2.5 bg-gradient-to-r from-[#FFD700] to-[#F5C800] text-[#002A4E] rounded-md transition-all font-medium flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Créer l'utilisateur
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'assignation de rôle */}
      {showRoleModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] overflow-y-auto animate-fadeIn">
          <div className="bg-white rounded-md p-8 max-w-lg w-full my-8 mx-4 animate-slideUp border border-slate-200">
              <h3 className="text-2xl font-bold text-gray-800 mb-4">
                Gérer les rôles de {selectedUser.prenom} {selectedUser.nom}
              </h3>

              {/* Rôles actuels */}
              <div className="mb-6">
                <p className="text-sm font-medium text-gray-600 mb-2">Rôles actuels:</p>
                <div className="flex flex-wrap gap-2">
                  {selectedUser.roles ? (
                    selectedUser.roles.split(',').map((role, idx) => (
                      <span
                        key={idx}
                        className={`px-3 py-1 rounded-full text-sm font-medium ${getRoleBadgeColor(role.trim())} flex items-center gap-2`}
                      >
                        {role.trim()}
                        <button
                          onClick={() => {
                            const roleObj = roles.find(r => r.name === role.trim())
                            if (roleObj) handleRemoveRole(selectedUser.users_id, roleObj.id)
                          }}
                          className="hover:bg-red-200 rounded-full p-0.5"
                        >
                          ✕
                        </button>
                      </span>
                    ))
                  ) : (
                    <span className="text-gray-500 text-sm">Aucun rôle assigné</span>
                  )}
                </div>
              </div>

              {/* Ajouter un rôle */}
              <div className="mb-6">
                <p className="text-sm font-medium text-gray-600 mb-2">Ajouter un rôle:</p>
                <div className="grid grid-cols-2 gap-2">
                  {roles.map(role => (
                    <button
                      key={role.id}
                      onClick={() => handleAssignRole(selectedUser.users_id, role.id)}
                      className="px-4 py-2 bg-purple-50 text-purple-700 rounded-md hover:bg-purple-100 transition-colors text-sm font-medium"
                    >
                      + {role.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Boutons */}
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowRoleModal(false)
                    setSelectedUser(null)
                  }}
                  className="px-6 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        )}

      {/* Modal de visualisation */}
      {showViewModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] overflow-y-auto animate-fadeIn">
          <div className="bg-white rounded-md p-8 max-w-2xl w-full my-8 mx-4 max-h-[90vh] overflow-y-auto animate-slideUp border border-slate-200">
              <div className="flex items-start justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-800">
                  Profil de l'utilisateur
                </h3>
                <button
                  onClick={() => {
                    setShowViewModal(false)
                    setSelectedUser(null)
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-6">
                {/* Photo de profil */}
                <div className="flex justify-center mb-6">
                  <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-3xl font-bold ">
                    {selectedUser.prenom?.[0]}{selectedUser.nom?.[0]}
                  </div>
                </div>

                {/* Informations personnelles */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gray-50 p-4 rounded-md">
                    <p className="text-sm text-gray-500 mb-1">Nom complet</p>
                    <p className="font-semibold text-gray-800">{selectedUser.prenom} {selectedUser.nom}</p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-md">
                    <p className="text-sm text-gray-500 mb-1">Email</p>
                    <p className="font-semibold text-gray-800">{selectedUser.email}</p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-md">
                    <p className="text-sm text-gray-500 mb-1">Téléphone</p>
                    <p className="font-semibold text-gray-800">{selectedUser.numero || 'N/A'}</p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-md">
                    <p className="text-sm text-gray-500 mb-1">Date d'inscription</p>
                    <p className="font-semibold text-gray-800">
                      {selectedUser.date_inscription 
                        ? new Date(selectedUser.date_inscription).toLocaleDateString('fr-FR', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })
                        : 'N/A'
                      }
                    </p>
                  </div>
                </div>

                {/* Rôles */}
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-4 rounded-md">
                  <p className="text-sm text-gray-600 mb-2 font-medium">Rôles</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedUser.roles ? (
                      selectedUser.roles.split(',').map((role, idx) => (
                        <span
                          key={idx}
                          className={`px-3 py-1 rounded-full text-sm font-medium ${getRoleBadgeColor(role.trim())}`}
                        >
                          {role.trim()}
                        </span>
                      ))
                    ) : (
                      <span className="text-gray-500 text-sm">Aucun rôle assigné</span>
                    )}
                  </div>
                </div>

                {/* Statistiques */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-blue-50 p-4 rounded-md text-center">
                    <p className="text-3xl font-bold text-blue-600">{selectedUser.post_count || 0}</p>
                    <p className="text-sm text-gray-600 mt-1">Publications</p>
                  </div>

                  <div className="bg-green-50 p-4 rounded-md text-center">
                    <p className="text-3xl font-bold text-green-600">
                      {selectedUser.archived ? '❌' : '✓'}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">Statut</p>
                  </div>

                  <div className="bg-purple-50 p-4 rounded-md text-center">
                    <p className="text-3xl font-bold text-purple-600">
                      {selectedUser.roles ? selectedUser.roles.split(',').length : 0}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">Rôles</p>
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-8">
                <button
                  onClick={() => {
                    setShowViewModal(false)
                    setSelectedUser(null)
                  }}
                  className="px-6 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors font-medium"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        )}

      {/* Modal d'archivage */}
      {showArchiveModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] overflow-y-auto animate-fadeIn">
          <div className="bg-white rounded-md p-8 max-w-md w-full my-8 mx-4 animate-scaleIn border border-slate-200">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-2">
                  {selectedUser.archived ? 'Désarchiver' : 'Archiver'} l'utilisateur
                </h3>
                <p className="text-gray-600">
                  {selectedUser.archived 
                    ? `Voulez-vous réactiver le compte de ${selectedUser.prenom} ${selectedUser.nom} ?`
                    : `Êtes-vous sûr de vouloir archiver le compte de ${selectedUser.prenom} ${selectedUser.nom} ?`
                  }
                </p>
              </div>

              <div className="bg-orange-50 border border-orange-200 rounded-md p-4 mb-6">
                <p className="text-sm text-orange-800">
                  {selectedUser.archived 
                    ? '✓ L\'utilisateur pourra à nouveau se connecter et publier.'
                    : '⚠️ L\'utilisateur ne pourra plus se connecter ni publier de contenu.'
                  }
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowArchiveModal(false)
                    setSelectedUser(null)
                  }}
                  className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors font-medium"
                >
                  Annuler
                </button>
                <button
                  onClick={() => handleArchiveUser(selectedUser.users_id, selectedUser.archived)}
                  className="flex-1 px-6 py-3 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors font-medium"
                >
                  {selectedUser.archived ? 'Désarchiver' : 'Archiver'}
                </button>
              </div>
            </div>
          </div>
        )}

      {/* Modal de suppression */}
      {showDeleteModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] overflow-y-auto animate-fadeIn">
          <div className="bg-white rounded-md p-8 max-w-md w-full my-8 mx-4 animate-shake border border-slate-200">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-2">
                  Supprimer l'utilisateur
                </h3>
                <p className="text-gray-600">
                  Êtes-vous sûr de vouloir supprimer définitivement le compte de{' '}
                  <span className="font-semibold">{selectedUser.prenom} {selectedUser.nom}</span> ?
                </p>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
                <p className="text-sm text-red-800 font-medium mb-2">⚠️ Action irréversible</p>
                <ul className="text-sm text-red-700 space-y-1">
                  <li>• Toutes les publications seront supprimées</li>
                  <li>• Les données de l'utilisateur seront perdues</li>
                  <li>• Cette action ne peut pas être annulée</li>
                </ul>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false)
                    setSelectedUser(null)
                  }}
                  className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors font-medium"
                >
                  Annuler
                </button>
                <button
                  onClick={() => handleDeleteUser(selectedUser.users_id, `${selectedUser.prenom} ${selectedUser.nom}`)}
                  className="flex-1 px-6 py-3 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors font-medium"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        )}
    </AdminLayout>
  )
}

export default AdminUsersPage
